import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBranding, resolveImageUrl } from '@/contexts/BrandingContext';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Building2, ChevronDown, ChevronUp, HelpCircle, ArrowRight, Phone, Mail, MessageCircle } from 'lucide-react';

export default function FaqPage() {
  const { login, isAuthenticated } = useAuth();
  const { branding } = useBranding();
  const faq = branding?.faq || {};
  const questions = faq.questions || [];
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background, #F5F4F0)', fontFamily: 'var(--font-body, Inter)' }}>
      {/* Navbar */}
      <nav className="bg-white border-b border-[var(--brand-border,#E7E5E4)] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            {branding?.logo_url ? (
              <img src={resolveImageUrl(branding.logo_url)} alt={branding?.site_name} className="w-10 h-10 rounded-lg object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--brand-primary, #264639)' }}>
                <Building2 className="w-6 h-6 text-white" />
              </div>
            )}
            <span className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading, Outfit)', color: 'var(--brand-text-primary, #1C1917)' }}>
              {branding?.site_name || 'Estadía 365'}
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/properties" className="text-sm font-medium hover:opacity-80" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>Propiedades</Link>
            <Link to="/about" className="text-sm font-medium hover:opacity-80" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>Sobre Nosotros</Link>
            {!isAuthenticated && (
              <Button onClick={login} style={{ backgroundColor: 'var(--brand-primary, #264639)', color: '#fff' }}>Iniciar Sesión</Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16" style={{ backgroundColor: 'var(--brand-primary, #264639)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading, Outfit)' }} data-testid="faq-title">
            {faq.title || 'Información para reservar'}
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed" data-testid="faq-description">
            {faq.description || 'Reservar tu estadía con Estadía 365 es simple, claro y seguro.'}
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center font-semibold uppercase tracking-[0.2em] text-xs mb-8" style={{ color: 'var(--brand-secondary, #C36F4D)' }}>
            Preguntas Frecuentes
          </p>
          <div className="space-y-3">
            {questions.map((item, idx) => (
              <div key={`faq-${item.q.substring(0, 20)}`} className="bg-white rounded-xl overflow-hidden shadow-sm" data-testid={`faq-item-${idx}`}>
                <button
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="font-medium pr-4" style={{ color: 'var(--brand-text-primary, #1C1917)', fontFamily: 'var(--font-heading, Outfit)' }}>
                    {item.q}
                  </span>
                  {openIndex === idx ? (
                    <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--brand-secondary, #C36F4D)' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--brand-text-muted, #78716C)' }} />
                  )}
                </button>
                {openIndex === idx && (
                  <div className="px-5 pb-5 border-t" style={{ borderColor: 'var(--brand-border, #E7E5E4)' }}>
                    <p className="pt-4 leading-relaxed" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'var(--font-heading, Outfit)', color: 'var(--brand-text-primary, #1C1917)' }}>
            ¿No encontraste lo que buscabas?
          </h2>
          <p className="mb-6" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>
            Contactanos directamente, te ayudamos a resolver cualquier duda.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            {branding?.social?.whatsapp && (
              <a href={`https://wa.me/${branding.social.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                <Button className="text-white px-6" style={{ backgroundColor: '#25D366' }} data-testid="faq-whatsapp-btn">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                </Button>
              </a>
            )}
            {branding?.contact?.email && (
              <a href={`mailto:${branding.contact.email}`}>
                <Button variant="outline" className="px-6" style={{ borderColor: 'var(--brand-primary, #264639)', color: 'var(--brand-primary, #264639)' }} data-testid="faq-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email
                </Button>
              </a>
            )}
            <Link to="/properties">
              <Button variant="outline" className="px-6" style={{ borderColor: 'var(--brand-secondary, #C36F4D)', color: 'var(--brand-secondary, #C36F4D)' }}>
                Ver Alojamientos <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1917] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {branding?.logo_url ? (
                <img src={resolveImageUrl(branding.logo_url)} alt="" className="w-8 h-8 rounded-lg object-contain" />
              ) : (
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--brand-primary, #264639)' }}>
                  <Building2 className="w-4 h-4 text-white" />
                </div>
              )}
              <span className="text-white font-semibold" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>{branding?.site_name || 'Estadía 365'}</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/" className="text-[#78716C] hover:text-white transition-colors">Inicio</Link>
              <Link to="/properties" className="text-[#78716C] hover:text-white transition-colors">Propiedades</Link>
              <Link to="/about" className="text-[#78716C] hover:text-white transition-colors">Sobre Nosotros</Link>
            </div>
          </div>
          <div className="border-t border-[#333] mt-6 pt-6 text-center">
            <p className="text-[#78716C] text-xs">{branding?.footer_text || '© Estadia 365'}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
